import React from 'react';
import {
  Action, ActionProps, ConfirmModal, ICON_CHECK, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { Agent, IndividualGoal } from '../../../providers/api/dtos';
import { createIndividualGoal } from '../../../providers/api/goal/goalProvider';

interface ChildFnProps {
  acceptAgentGoal: VoidFunction,
  isSaving: boolean,
}

export interface AcceptAgentGoalActionProps extends Pick<ActionProps, 'disabled' | 'loading'> {
  agentId: Agent['id'];
  target: IndividualGoal['target'];
  onSuccess?: (goal: IndividualGoal) => void;
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

export const ACCEPT_AGENT_GOAL_SUCCESS_MESSAGE = 'Obiettivo di guadagno salvato correttamente.';
export const ACCEPT_AGENT_GOAL_ERROR_MESSAGE = 'Non è stato possibile salvare l\'obiettivo di guadagno.';

/**
 * @todo rename to `AcceptIndividualGoalAction`
 */
export const AcceptAgentGoalAction: React.FC<AcceptAgentGoalActionProps> = ({
  agentId,
  target,
  loading,
  disabled,
  onSuccess,
  children,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const confirmAcceptAgentGoalModal = useModal();

  const openConfirmModal = React.useCallback(() => {
    confirmAcceptAgentGoalModal.open();
  }, [confirmAcceptAgentGoalModal]);

  const acceptAgentGoal = React.useCallback(async () => {
    setIsSaving(true);

    try {
      const goal = await createIndividualGoal({ agentId, target });
      confirmAcceptAgentGoalModal.close();

      addSuccess(ACCEPT_AGENT_GOAL_SUCCESS_MESSAGE);
      onSuccess?.(goal);
    } catch (error: unknown) {
      addError(ACCEPT_AGENT_GOAL_ERROR_MESSAGE);
      return;
    } finally {
      setIsSaving(false);
    }
  }, [agentId, target, confirmAcceptAgentGoalModal, addSuccess, onSuccess, addError]);

  return (
    <>
      {
        children
          ? children({ acceptAgentGoal: openConfirmModal, isSaving })
          : (
            <Action
              label="Accetta obiettivo"
              aria-label="Pulsante per accettare l'obiettivo di guadagno"
              color="primary"
              emphasis="high"
              iconLeft={{ path: ICON_CHECK }}
              loading={loading || isSaving}
              disabled={disabled}
              onClick={openConfirmModal}
            />
          )
      }
      <Portal>
        <ConfirmModal
          isOpen={confirmAcceptAgentGoalModal.isOpen}
          title="Accetta l'obiettivo di guadagno"
          aria-label="Accetta l'obiettivo di guadagno"
          onConfirm={acceptAgentGoal}
          onAbort={confirmAcceptAgentGoalModal.close}
        >
          Accettando l&apos;obiettivo di guadagno di {formatEuro(target)} potrai monitorare le tue performance rispetto alle metriche stabilite. L’obiettivo sarà visibile dall’area manager/broker e potrà essere modificato successivamente.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default AcceptAgentGoalAction;
